import React, { useCallback, useEffect, useState } from "react";
import { Wallet, CheckCircle2, Clock, Timer, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { apiClient } from "@/lib/api";
import { formatGNF } from "@/utils/format";
import { KpiCard, RangeSelector } from "@/components/dashboard/DashboardWidgets";
import ExportButtons from "@/components/dashboard/ExportButtons";
import { downloadCsv, downloadPdf, slugify, todayStamp } from "@/utils/exportHelpers";

/**
 * Shared driver dashboard widget.
 * @param {string} [driverId] – admin view. Otherwise inferred from current user.
 */
export default function DriverDashboardWidget({ driverId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ days: String(days) });
    if (driverId) qs.set("driver_id", driverId);
    apiClient.get(`/dashboard/driver?${qs.toString()}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [days, driverId]);

  const driverName = data?.driver?.name || (driverId ? driverId.slice(-6) : "driver");
  const filenameBase = `neldaari-driver-${slugify(driverName)}-${days}j-${todayStamp()}`;

  const buildCsv = useCallback(() => {
    if (!data) return;
    const k = data.kpis || {};
    downloadCsv(`${filenameBase}.csv`, [
      {
        title: `${t("dashboard.driverTitle")} — ${driverName}`,
        headers: [t("dashboard.pdfPeriod"), `${days}j`],
        rows: [],
      },
      {
        title: t("dashboard.pdfSection.kpis"),
        headers: [t("dashboard.earnings"), t("dashboard.deliveries"), t("dashboard.avgTime"), t("dashboard.onTimeRate")],
        rows: [[k.earnings ?? 0, k.deliveries ?? 0, `${k.avg_delivery_min ?? 0} min`, k.on_time_rate != null ? `${k.on_time_rate}%` : "—"]],
      },
      {
        title: t("dashboard.pdfSection.series"),
        headers: ["date", t("dashboard.earnings"), t("dashboard.deliveries")],
        rows: (data.series || []).map((s) => [s.date, s.earnings ?? 0, s.deliveries ?? 0]),
      },
      {
        title: t("dashboard.pdfSection.history"),
        headers: ["#", t("dashboard.colRestaurant"), "Zone", t("dashboard.earnings"), t("dashboard.avgTime"), t("dashboard.onTime")],
        rows: (data.history || []).map((o) => [
          o.order_id.slice(-6).toUpperCase(),
          o.restaurant_name || "",
          o.zone || "",
          o.earnings ?? 0,
          o.duration_min != null ? `${o.duration_min} min` : "",
          o.on_time === true ? "✓" : o.on_time === false ? "✗" : "—",
        ]),
      },
    ]);
  }, [data, days, driverName, filenameBase, t]);

  const buildPdf = useCallback(() => {
    if (!data) return;
    const k = data.kpis || {};
    const now = new Date();
    downloadPdf(`${filenameBase}.pdf`, {
      title: `${t("dashboard.driverTitle")} — ${driverName}`,
      subtitle: t("dashboard.driverSubtitle"),
      period: `${t("dashboard.pdfPeriod")}: ${days}${lang === "en" ? "d" : "j"}`,
      generatedLabel: `${t("dashboard.pdfGeneratedOn")} ${now.toLocaleString(lang === "en" ? "en-US" : "fr-FR")}`,
      sections: [
        {
          title: t("dashboard.pdfSection.kpis"),
          kvs: [
            [t("dashboard.earnings"), formatGNF(k.earnings)],
            [t("dashboard.deliveries"), k.deliveries ?? 0],
            [t("dashboard.avgTime"), `${k.avg_delivery_min ?? 0} min`],
            [t("dashboard.onTimeRate"), k.on_time_rate != null ? `${k.on_time_rate}%` : "—"],
          ],
        },
        {
          title: t("dashboard.pdfSection.series"),
          headers: ["date", t("dashboard.earnings"), t("dashboard.deliveries")],
          rows: (data.series || []).map((s) => [s.date, formatGNF(s.earnings), s.deliveries ?? 0]),
        },
        {
          title: t("dashboard.pdfSection.history"),
          headers: ["#", t("dashboard.colRestaurant"), "Zone", t("dashboard.earnings"), t("dashboard.avgTime"), t("dashboard.onTime")],
          rows: (data.history || []).map((o) => [
            o.order_id.slice(-6).toUpperCase(),
            o.restaurant_name || "",
            o.zone || "",
            formatGNF(o.earnings || 0),
            o.duration_min != null ? `${o.duration_min} min` : "",
            o.on_time === true ? t("dashboard.onTime") : o.on_time === false ? t("dashboard.late") : "—",
          ]),
        },
      ],
    });
  }, [data, days, driverName, filenameBase, lang, t]);

  if (loading || !data) return <div className="text-mute text-sm py-10">{t("dashboard.loading")}</div>;

  const k = data.kpis;
  const sharePct = Math.round((data.share || 0.7) * 100);
  const onTimeColor = k.on_time_rate == null ? "#4A4A4A" : k.on_time_rate >= 85 ? "#009E60" : k.on_time_rate >= 70 ? "#F4C430" : "#E32636";

  return (
    <div className="space-y-6" data-testid="driver-dashboard-widget">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-red">{t("dashboard.driverShareBadge", { pct: sharePct })}</div>
          <h2 className="font-display text-2xl font-semibold mt-1">{t("dashboard.driverPerformanceTitle", { n: days })}</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButtons onExportPdf={buildPdf} onExportCsv={buildCsv} testidPrefix="drv" />
          <RangeSelector value={days} onChange={setDays} testidPrefix="drv-range" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={<Wallet className="w-4 h-4" />} label={t("dashboard.earnings")} value={formatGNF(k.earnings)} growthPct={k.growth.earnings_pct} color="#009E60" testid="drv-earnings" />
        <KpiCard icon={<CheckCircle2 className="w-4 h-4" />} label={t("dashboard.deliveries")} value={k.deliveries} growthPct={k.growth.deliveries_pct} color="#E32636" testid="drv-deliveries" />
        <KpiCard icon={<Timer className="w-4 h-4" />} label={t("dashboard.avgTime")} value={`${k.avg_delivery_min} min`} color="#F4C430" testid="drv-avg-time" />
        <div className="bg-white border border-sand rounded-2xl p-4" data-testid="drv-ontime">
          <div className="text-xs uppercase tracking-widest mb-1 flex items-center gap-1.5" style={{ color: onTimeColor }}>
            <Clock className="w-4 h-4" /> {t("dashboard.onTimeRate")}
          </div>
          <div className="font-display text-xl sm:text-2xl font-semibold">
            {k.on_time_rate != null ? `${k.on_time_rate}%` : "—"}
          </div>
          <div className="text-[11px] text-mute mt-1">
            {k.on_time_rate == null ? t("dashboard.onTimeNoData") :
              k.on_time_rate >= 85 ? t("dashboard.onTimeExcellent") :
              k.on_time_rate >= 70 ? t("dashboard.onTimeOk") : t("dashboard.onTimeBad")}
          </div>
        </div>
      </div>

      {/* Earnings chart */}
      <div className="bg-white border border-sand rounded-2xl p-4">
        <h3 className="font-semibold mb-3">{t("dashboard.dailyEarnings")}</h3>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <AreaChart data={data.series} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <defs>
                <linearGradient id="drvEarn" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#009E60" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#009E60" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBE8E0" />
              <XAxis dataKey="date" fontSize={11} tickFormatter={(d) => d.slice(5)} />
              <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatGNF(v)} />
              <Area type="monotone" dataKey="earnings" name={t("dashboard.earnings")} stroke="#009E60" fill="url(#drvEarn)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent history */}
      <div className="bg-white border border-sand rounded-2xl p-4" data-testid="drv-history">
        <h3 className="font-semibold mb-3">{t("dashboard.recentDeliveries")}</h3>
        {data.history.length === 0 ? (
          <div className="text-mute text-sm">{t("dashboard.noDeliveries")}</div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
            {data.history.map((o, i) => (
              <div key={o.order_id} data-testid={`drv-hist-${i}`} className="flex items-start justify-between gap-3 py-2 border-b border-sand/60 last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] text-mute">#{o.order_id.slice(-6).toUpperCase()}</span>
                    <span className="font-semibold text-sm truncate">{o.restaurant_name}</span>
                    {o.on_time === true && <span className="text-[10px] uppercase tracking-widest bg-[#E7F6EE] text-brand-green px-2 py-0.5 rounded-full">{t("dashboard.onTime")}</span>}
                    {o.on_time === false && <span className="text-[10px] uppercase tracking-widest bg-[#FFF5F6] text-brand-red px-2 py-0.5 rounded-full">{t("dashboard.late")}</span>}
                  </div>
                  <div className="text-xs text-mute mt-0.5 flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3 shrink-0" /> {o.address} · {o.zone}
                    {o.duration_min && <span className="ml-2">· {o.duration_min} min</span>}
                  </div>
                </div>
                <span className="font-semibold text-sm text-brand-green shrink-0">{formatGNF(o.earnings || 0)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
